import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export default function HeroSection() {
  const { t } = useTranslation("home");
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      ref={ref}
      style={{
        minHeight: "100vh",
        background: "#FAF5E8",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Background decorative circles */}
      <div
        style={{
          position: "absolute",
          top: -120,
          right: -120,
          width: 520,
          height: 520,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(245,237,214,0.95) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: -160,
          left: -160,
          width: 600,
          height: 600,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(245,237,214,0.7) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      {/* Side botanical image */}
      <div
        className="hidden lg:block"
        style={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          width: "38%",
          overflow: "hidden",
          opacity: visible ? 1 : 0,
          transition: "opacity 1.2s ease",
        }}
      >
        <img
          src="https://readdy.ai/api/search-image?query=Lush%20tropical%20botanical%20leaves%20and%20plants%20deep%20forest%20green%20foliage%20elegant%20minimalist%20composition%20soft%20natural%20lighting%20high-end%20editorial%20style%20photography%20sophisticated%20nature%20and%20language%20education%20symbol%20modern%20clean%20serene%20atmosphere%20organic%20shapes&width=700&height=900&seq=hero_botanical_side_1&orientation=portrait"
          alt="botanical"
          className="w-full h-full object-cover object-top"
          style={{ opacity: 0.6 }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to right, #FAF5E8 0%, rgba(250,245,232,0.0) 60%)",
          }}
        />
      </div>

      {/* Main Content */}
      <div
        className="relative z-10 w-full max-w-6xl mx-auto px-6 flex flex-col lg:flex-row items-center"
        style={{ paddingTop: 120, paddingBottom: 80 }}
      >
        <div
          className="flex flex-col items-start lg:items-start"
          style={{
            maxWidth: 580,
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(28px)",
            transition: "opacity 0.9s ease, transform 0.9s ease",
          }}
        >
          {/* Tag */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 14px",
              borderRadius: 999,
              background: "rgba(245,237,214,0.7)",
              marginBottom: 28,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "#2C5F4F",
                display: "inline-block",
              }}
            />
            <span
              style={{
                fontFamily: "'Noto Sans JP', sans-serif",
                fontSize: 12,
                fontWeight: 500,
                color: "#2C5F4F",
                letterSpacing: "0.06em",
              }}
            >
              {t("hero.tag")}
            </span>
          </div>

          {/* Catchphrase */}
          <h1
            style={{
              fontFamily: "'Noto Sans JP', sans-serif",
              fontWeight: 900,
              fontSize: "clamp(36px, 4.5vw, 56px)",
              lineHeight: 1.35,
              color: "#1A1A1A",
              letterSpacing: "0.02em",
              marginBottom: 28,
            }}
          >
            <span style={{ color: "#2C5F4F" }}>{t("hero.titleHighlight")}</span>
            {t("hero.titleRest")}
            <br />
            {t("hero.titleLine2")}
          </h1>

          {/* Subtext */}
          <p
            style={{
              fontFamily: "'Noto Serif JP', serif",
              fontSize: 16,
              lineHeight: 2.0,
              color: "#555555",
              maxWidth: 520,
              marginBottom: 44,
              opacity: visible ? 1 : 0,
              transition: "opacity 1.1s ease 0.3s",
            }}
          >
            {t("hero.subtext")}
          </p>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <Link
              to="/register"
              className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
              style={{
                padding: "14px 32px",
                borderRadius: 12,
                background: "#2C5F4F",
                color: "#fff",
                fontFamily: "'Noto Sans JP', sans-serif",
                fontWeight: 600,
                fontSize: 15,
                transition: "background 0.2s, transform 0.2s, box-shadow 0.2s",
                boxShadow: "0 4px 20px rgba(44,95,79,0.25)",
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLAnchorElement;
                el.style.background = "#3A7A63";
                el.style.transform = "translateY(-2px)";
                el.style.boxShadow = "0 8px 28px rgba(44,95,79,0.32)";
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLAnchorElement;
                el.style.background = "#2C5F4F";
                el.style.transform = "translateY(0)";
                el.style.boxShadow = "0 4px 20px rgba(44,95,79,0.25)";
              }}
            >
              {t("hero.ctaRegister")}
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/services"
              className="whitespace-nowrap cursor-pointer inline-flex items-center gap-1"
              style={{
                padding: "14px 20px",
                fontFamily: "'Noto Sans JP', sans-serif",
                fontWeight: 500,
                fontSize: 14,
                color: "#2C5F4F",
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLAnchorElement).style.opacity = "0.7")
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLAnchorElement).style.opacity = "1")
              }
            >
              {t("hero.ctaServices")}
              <i className="ri-external-link-line" style={{ fontSize: 13 }} />
            </Link>
          </div>

          {/* Stats */}
          <div
            className="flex gap-8 mt-12 pt-10"
            style={{
              borderTop: "1px solid rgba(180,150,90,0.15)",
              opacity: visible ? 1 : 0,
              transition: "opacity 1.2s ease 0.5s",
            }}
          >
            {[
              { num: "3", label: t("hero.stats.categories") },
              { num: "SLA", label: t("hero.stats.slaExperts") },
              { num: "∞", label: t("hero.stats.possibilities") },
            ].map((stat) => (
              <div key={stat.label}>
                <p
                  style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontWeight: 700,
                    fontSize: 28,
                    color: "#2C5F4F",
                    lineHeight: 1,
                    marginBottom: 4,
                  }}
                >
                  {stat.num}
                </p>
                <p
                  style={{
                    fontFamily: "'Noto Sans JP', sans-serif",
                    fontSize: 12,
                    color: "#888888",
                  }}
                >
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        style={{
          position: "absolute",
          bottom: 32,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 6,
          opacity: 0.4,
        }}
      >
        <span
          style={{
            fontFamily: "'Noto Sans JP', sans-serif",
            fontSize: 10,
            letterSpacing: "0.12em",
            color: "#2C5F4F",
          }}
        >
          {t("hero.scroll")}
        </span>
        <div
          style={{
            width: 1,
            height: 40,
            background: "linear-gradient(to bottom, #2C5F4F, transparent)",
          }}
        />
      </div>
    </section>
  );
}
